// Sonidos sintetizados: tonos DTMF del teclado y timbre de llamada entrante (440+480 Hz, como en
// Norteamerica/Colombia). Sin archivos de audio.

let ctx: AudioContext | null = null;

export function audioContext() {
  try {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = ctx || new Ctor();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

const DTMF: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

function tone(freqs: number[], dur = 0.12, vol = 0.045) {
  const a = audioContext();
  if (!a) return;
  try {
    const g = a.createGain();
    const t = a.currentTime;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.01);
    g.gain.setValueAtTime(vol, t + dur - 0.03);
    g.gain.linearRampToValueAtTime(0, t + dur);
    g.connect(a.destination);
    for (const f of freqs) {
      const o = a.createOscillator();
      o.frequency.value = f;
      o.connect(g);
      o.start(t);
      o.stop(t + dur);
    }
  } catch {
    /* audio no disponible */
  }
}

export const keyTone = (tecla: string) => {
  const f = DTMF[tecla];
  if (f) tone(f);
};

// Timbre de llamada entrante (D68). Chrome deja mudo el audio hasta que la persona toca la pagina: antes el contexto
// de audio nacia al entrar la llamada (sin un clic) y el timbre no sonaba. Ahora se desbloquea con el primer clic o
// tecla tras entrar a la Consola, y hay un timbre de respaldo en un <audio> por si el sintetizador sigue suspendido.
let ringTimer: ReturnType<typeof setInterval> | null = null;
let sonando = false;
let volumen = 0.6;
let respaldo: HTMLAudioElement | null = null;

const ganancia = () => 0.015 + volumen * 0.11;

export function fijarVolumenTimbre(v: number) {
  volumen = Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0.6));
  if (respaldo) respaldo.volume = volumen;
}

// WAV de un ciclo del timbre (1,2 s de 440+480 Hz y 1,8 s de silencio), generado en memoria.
export function wavTimbre(muestreo = 8000) {
  const n = muestreo * 3;
  const datos = new Uint8Array(44 + n);
  const v = new DataView(datos.buffer);
  const txt = (o: number, t: string) => [...t].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  txt(0, 'RIFF');
  v.setUint32(4, 36 + n, true);
  txt(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, muestreo, true);
  v.setUint32(28, muestreo, true);
  v.setUint16(32, 1, true);
  v.setUint16(34, 8, true);
  txt(36, 'data');
  v.setUint32(40, n, true);
  const sonido = muestreo * 1.2;
  for (let i = 0; i < n; i++) {
    const t = i / muestreo;
    const env = i < sonido ? Math.min(1, i / 80, (sonido - i) / 80) : 0;
    datos[44 + i] = 128 + Math.round(env * 50 * (Math.sin(2 * Math.PI * 440 * t) + Math.sin(2 * Math.PI * 480 * t)));
  }
  return datos;
}

function respaldoAudio() {
  if (respaldo || typeof Audio === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return respaldo;
  try {
    respaldo = new Audio(URL.createObjectURL(new Blob([wavTimbre()], { type: 'audio/wav' })));
    respaldo.loop = true;
    respaldo.volume = volumen;
  } catch {
    respaldo = null;
  }
  return respaldo;
}

export const audioDesbloqueado = () => ctx?.state === 'running';

// Llamar dentro de un clic o una tecla: despierta el sintetizador y deja listo el timbre de respaldo.
export function desbloquearAudio() {
  const a = audioContext();
  const r = respaldoAudio();
  if (r && !sonando) {
    r.muted = true;
    Promise.resolve(r.play())
      .then(() => {
        if (sonando) return;
        r.pause();
        r.currentTime = 0;
        r.muted = false;
      })
      .catch(() => (r.muted = false));
  }
  return a?.state === 'running';
}

// Una vez por sesion: el primer clic o tecla en cualquier parte desbloquea el audio.
let instalado = false;
export function instalarDesbloqueo() {
  if (instalado || typeof window === 'undefined') return;
  instalado = true;
  const f = () => {
    desbloquearAudio();
    if (audioDesbloqueado()) {
      window.removeEventListener('pointerdown', f, true);
      window.removeEventListener('keydown', f, true);
    }
  };
  window.addEventListener('pointerdown', f, true);
  window.addEventListener('keydown', f, true);
}

export function startRingtone() {
  stopRingtone();
  sonando = true;
  const a = audioContext();
  if (a && a.state === 'running') {
    const ring = () => tone([440, 480], 1.2, ganancia());
    ring();
    ringTimer = setInterval(ring, 3000);
    return;
  }
  // Sintetizador suspendido: suena el <audio> de respaldo (funciona si la pagina ya recibio algun clic).
  const r = respaldoAudio();
  if (r) {
    r.muted = false;
    r.currentTime = 0;
    Promise.resolve(r.play()).catch(() => undefined);
  }
}

export function stopRingtone() {
  sonando = false;
  if (ringTimer) clearInterval(ringTimer);
  ringTimer = null;
  if (respaldo && !respaldo.paused) {
    respaldo.pause();
    respaldo.currentTime = 0;
  }
}

// "Probar timbre" en los ajustes del telefono: un solo ciclo.
export function probarTimbre() {
  desbloquearAudio();
  tone([440, 480], 1.2, ganancia());
}

// Tono corto de "llamada en espera" (segunda llamada entrante mientras hablas).
export const waitingBeep = () => tone([440], 0.25, 0.04);

// Sonidos de la campana (D52): notas cortas que se apagan solas, como una campanilla.
function nota(f: number, inicio: number, dur: number, vol: number, forma: OscillatorType = 'triangle') {
  const a = audioContext();
  if (!a) return;
  try {
    const t = a.currentTime + inicio;
    const o = a.createOscillator();
    const g = a.createGain();
    o.type = forma;
    o.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(a.destination);
    o.start(t);
    o.stop(t + dur + 0.05);
  } catch {
    /* audio no disponible */
  }
}

export function sonarAviso(s: 'suave' | 'campanilla' | 'reunion' | 'ninguno') {
  if (s === 'suave') {
    nota(880, 0, 0.28, 0.08, 'sine');
    nota(1320, 0, 0.18, 0.025, 'sine');
  }
  if (s === 'campanilla') {
    nota(784, 0, 0.35, 0.09);
    nota(1175, 0.13, 0.5, 0.09);
  }
  if (s === 'reunion') {
    nota(659, 0, 0.3, 0.11);
    nota(880, 0.16, 0.3, 0.11);
    nota(1319, 0.32, 0.7, 0.11);
  }
}
